import { EBotType, IClaimDetails } from '../types';

export const getCashlessPayload = (data: IClaimDetails) => {
  return {
    claim_details: {
      plumClaimId: {
        description: 'This is the claimId',
        value: data?.plumClaimId ?? null,
      },
      requestedClaimAmount: {
        description: 'This is the treatment amount requested by the hospital',
        value: data?.amount === '0' ? null : data?.amount ?? null,
      },
      approvedClaimAmountForCurrentStage: {
        description:
          'This is the amount approved by the insurer for the current stage.',
        value: data?.amountApproved ?? null,
      },
      currentStage: {
        description: 'This is the current stage of the cashless claim.',
        value: data?.stage ?? null,
      },
      currentStageDate: {
        description:
          'This is the date and time at which the current stage happened',
        value: data?.fullfilled?.[data?.stage]?.date ?? null,
      },
      approvalLetterURL: {
        description: 'This is the approval letter from the insurer',
        value: null,
      },
      cashlessClaimRejectionReasonByInsurer: {
        description:
          'This is the reason for the rejection of the cashless claim by the insurer.',
        value: data?.insurerRejectionReason ?? null,
      },
      nextExpectedStage: {
        description: 'This is the next expected stage of the cashless claim.',
        value: data?.nextExpectedStage ?? null,
      },
      nextExpectedStageDate: {
        description:
          'This is the dateTime at which the claim would progress to next expected stage.',
        value: data?.nextExpectedStageDate ?? null,
      },
    },
  };
};

type ClaimLike =
  | {
      plumClaimId?: string;
      memberName?: string;
      uiRepresentation?: { status?: { valueCaps?: string } };
    }
  | null
  | undefined;

/**
 * Builds the v3 cashless_support_bot contextValues from the claim the claims
 * widget hands over via the CB_CLAIM_DETAILS postMessage. Mirrors the scoped
 * contract the Support Center sets on the cookie (cashless_claim + platform) so
 * the backend resolves the cashless domain and keeps mobile-vs-web behaviour.
 */
export const buildCashlessClaimContext = (claim: ClaimLike, platform: string) => ({
  platform,
  cashless_claim: {
    current_claim: {
      plumClaimId: claim?.plumClaimId,
      memberName: claim?.memberName,
      status: claim?.uiRepresentation?.status?.valueCaps,
    },
  },
});

/**
 * Whether a strapi-article click should be posted to the parent frame (embedded
 * iframe widgets) instead of opened in-page. cashless_support_bot uses the
 * widget behaviour only when embedded — i.e. not launched full-page from the
 * Support Center (which sets parent=support-center).
 */
export const shouldPostArticleToParent = (
  botName: string | undefined,
  parent: string | null,
): boolean =>
  botName === EBotType.CASHLESS_GPT_V2 ||
  botName === EBotType.RTL_BOT ||
  (botName === EBotType.CASHLESS_SUPPORT_BOT && parent !== 'support-center');

/**
 * Whether the back button should close the embedding iframe via a CLOSE_IFRAME
 * postMessage — the legacy cashless widget, RTL, and the v3 cashless widget.
 * (Full-page launches are handled earlier by the parent-param branches.)
 */
export const usesIframeCloseBehaviour = (
  botName: string | undefined,
): boolean =>
  botName === EBotType.CASHLESS_GPT_V2 ||
  botName === EBotType.CASHLESS_SUPPORT_BOT ||
  botName === EBotType.CULT_SUPPORT_BOT ||
  botName === EBotType.RTL_BOT;
